import { useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorCard } from "@/components/CalculatorCard";
import { DollarSign, TrendingUp, PiggyBank, CreditCard, LineChart, Building, Coins, BarChart3, Percent, TrendingDown, Wallet, Calculator, Grid3x3, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AdSenseAd } from "@/components/AdSenseAd";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";
import { useTranslation } from "react-i18next";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema } from "@/utils/structuredData";

const basicCalculators = {
  basicFinance: [
    {
      title: "Simple Interest Calculator",
      description: "Calculate interest earned or owed on principal",
      icon: DollarSign,
      href: "/calculator/simple-interest",
      category: "finance" as const,
    },
    {
      title: "Compound Interest Calculator",
      description: "Interest with compounding periods",
      icon: TrendingUp,
      href: "/calculator/compound-interest",
      category: "finance" as const,
    },
    {
      title: "Future Value Calculator",
      description: "Future value of current investment",
      icon: TrendingUp,
      href: "/calculator/future-value",
      category: "finance" as const,
    },
    {
      title: "Savings Calculator",
      description: "Monthly savings needed for a goal",
      icon: PiggyBank,
      href: "/calculator/savings",
      category: "finance" as const,
    },
    {
      title: "Break-Even Analysis",
      description: "When revenue equals costs",
      icon: BarChart3,
      href: "/calculator/break-even",
      category: "finance" as const,
    },
    {
      title: "Profit Margin Calculator",
      description: "Gross and net profit margins",
      icon: Percent,
      href: "/calculator/profit-margin",
      category: "finance" as const,
    },
    {
      title: "Depreciation Calculator",
      description: "Asset value decline over time",
      icon: TrendingDown,
      href: "/calculator/depreciation",
      category: "finance" as const,
    },
  ],
  investments: [
    {
      title: "ROI Calculator",
      description: "Return on Investment percentage",
      icon: TrendingUp,
      href: "/calculator/roi",
      category: "finance" as const,
    },
    {
      title: "Bond Yield Calculator",
      description: "YTM, YTC, and current yield",
      icon: Coins,
      href: "/calculator/bond-yield",
      category: "finance" as const,
    },
  ],
};

const advancedCalculators = [
  {
    title: "Black-Scholes Calculator",
    description: "European option pricing model",
    icon: LineChart,
    href: "/calculator/black-scholes",
    category: "finance" as const,
  },
  {
    title: "Duration & Convexity",
    description: "Bond price sensitivity to interest rates",
    icon: LineChart,
    href: "/calculator/duration-convexity",
    category: "finance" as const,
  },
  {
    title: "CAPM Calculator",
    description: "Expected return using Capital Asset Pricing Model",
    icon: TrendingUp,
    href: "/calculator/capm",
    category: "finance" as const,
  },
  {
    title: "Sharpe Ratio Calculator",
    description: "Calculate risk-adjusted returns",
    icon: BarChart3,
    href: "/calculator/sharpe-ratio",
    category: "finance" as const,
  },
  {
    title: "Value at Risk (VaR)",
    description: "Portfolio risk estimation",
    icon: TrendingDown,
    href: "/calculator/var",
    category: "finance" as const,
  },
  {
    title: "Present Value Calculator",
    description: "Current value of future cash flows",
    icon: DollarSign,
    href: "/calculator/present-value",
    category: "finance" as const,
  },
  {
    title: "Present Value of Annuity",
    description: "PV of regular payment streams",
    icon: Wallet,
    href: "/calculator/annuity-pv",
    category: "finance" as const,
  },
  {
    title: "Internal Rate of Return",
    description: "Investment profitability measure",
    icon: TrendingUp,
    href: "/calculator/irr",
    category: "finance" as const,
  },
  {
    title: "Net Present Value",
    description: "NPV with discount rate",
    icon: DollarSign,
    href: "/calculator/npv",
    category: "finance" as const,
  },
  {
    title: "Modified Duration",
    description: "Interest rate risk measure for bonds",
    icon: LineChart,
    href: "/calculator/modified-duration",
    category: "finance" as const,
  },
  {
    title: "Option Greeks Calculator",
    description: "Delta, Gamma, Vega, Theta, Rho",
    icon: Calculator,
    href: "/calculator/option-greeks",
    category: "finance" as const,
  },
  {
    title: "Binomial Options Pricing",
    description: "American option valuation model",
    icon: Grid3x3,
    href: "/calculator/binomial-options",
    category: "finance" as const,
  },
  {
    title: "Time Value of Money",
    description: "Comprehensive TVM calculations",
    icon: Calculator,
    href: "/calculator/time-value-money",
    category: "finance" as const,
  },
  {
    title: "Actuarial Present Value",
    description: "Life insurance and annuity valuations",
    icon: Wallet,
    href: "/calculator/actuarial-pv",
    category: "finance" as const,
  },
  {
    title: "Life Table Calculator",
    description: "Mortality rates and life expectancy",
    icon: BarChart3,
    href: "/calculator/life-table",
    category: "finance" as const,
  },
  {
    title: "Portfolio Beta Calculator",
    description: "Measure systematic risk of portfolio",
    icon: LineChart,
    href: "/calculator/portfolio-beta",
    category: "finance" as const,
  },
];

export default function Finance() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("basic");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: t('categories.pages.finance.title'), url: "https://calclab.app/finance" }
    ], lang)
  ]);

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('categories.pages.finance.title')}</h1>
          <p className="text-muted-foreground">
            {t('categories.pages.finance.description')}
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">{t('categories.pages.finance.basic')}</TabsTrigger>
              <TabsTrigger value="advanced">{t('categories.pages.finance.advanced')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {activeTab === "basic" ? (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold mb-4">{t('categories.pages.finance.basicSectionTitle')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {basicCalculators.basicFinance.map((calc) => (
                  <CalculatorCard key={calc.href} {...calc} />
                ))}
              </div>
            </div>

            <AdSenseAd slot="1234567890" />

            <div>
              <h2 className="text-2xl font-semibold mb-4">{t('categories.pages.finance.investmentsSectionTitle')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {basicCalculators.investments.map((calc) => (
                  <CalculatorCard key={calc.href} {...calc} />
                ))}
              </div>
            </div>
          </div>
        ) : (

          <div className="space-y-8">
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>{t('categories.pages.finance.warningTitle')}</strong> {t('categories.pages.finance.warningContent')}
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold mb-4">{t('categories.pages.finance.advancedSectionTitle')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advancedCalculators.map((calc) => (
                  <CalculatorCard key={calc.href} {...calc} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
